import { Instance, SnapshotIn, types, flow, getEnv, getSnapshot, applySnapshot } from 'mobx-state-tree';

import { extractResolvedMediaUrl, performGetExecution, performResolveExecutionMedia } from 'NeoAnalytics/services';
import { IStoresEnv } from '@core/storesEnv';

const StepDetail = types.frozen<Record<string, unknown>>();

const ExecutionStep = types.model({
    id: types.string,
    EventId: types.maybeNull(types.string),
    eventId: types.maybeNull(types.string),
    actorId: types.string,
    name: types.string,
    description: types.maybeNull(types.string),
    startTime: types.string,
    endTime: types.maybeNull(types.string),
    duration: types.maybeNull(types.string),
    parentStepId: types.maybeNull(types.string),
    details: types.maybeNull(StepDetail),
    status: types.maybeNull(types.string),
    completionStatus: types.maybeNull(types.string),
    stepCompletionStatus: types.maybeNull(types.string),
    result: types.maybeNull(types.string),
    success: types.maybeNull(types.boolean),
})

const ExecutionEvent = types.model({
    actorId: types.string,
    name: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
    type: types.maybeNull(types.string),
    parentEventId: types.maybeNull(types.string),
    value: types.maybeNull(types.frozen<Record<string, unknown>>()),
    timestamp: types.maybeNull(types.string),
})

const MediaEvent = types.frozen<Record<string, unknown>>();

export const ExecutionsInitialState = {
    id: '',
    actorIds: [],
    additionalData: null,
    executionMode: '',
    platform: '',
    startTime: null,
    endTime: null,
    duration: null,
    endReason: '',
    isCompleted: null,
    steps: [],
    success: null,
    events: [],
    totalSteps: null,
    totalErrors: null,
    entityId: '',
    entityName: '',
    entityDescription: '',
    entityVersion: '',
    entityAdditionalData: null,
    companyId: '',
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
    mediaEvents: [],
}

export const ExecutionsStoreInferred = types
    .model('ExecutionsStoreInferred', {
        id: types.optional(types.string, ''),
        actorIds: types.optional(types.array(types.string), []),
        additionalData: types.maybeNull(types.frozen<Record<string, unknown>>()),
        platform: types.maybeNull(types.string),
        startTime: types.maybeNull(types.string),
        endReason: types.maybeNull(types.string),
        endTime: types.maybeNull(types.string),
        entityId: types.maybeNull(types.string),
        executionMode: types.maybeNull(types.string),
        duration: types.maybeNull(types.string),
        isCompleted: types.maybeNull(types.boolean),
        steps: types.optional(types.array(ExecutionStep), []),
        events: types.optional(types.array(ExecutionEvent), []),
        totalSteps: types.maybeNull(types.integer),
        totalErrors: types.maybeNull(types.integer),
        entityAdditionalData: types.maybeNull(types.frozen<Record<string, unknown>>()),
        entityName: types.maybeNull(types.string),
        entityDescription: types.maybeNull(types.string),
        entityVersion: types.maybeNull(types.string),
        companyId: types.maybeNull(types.string),
        createdAt: types.maybeNull(types.string),
        createdBy: types.maybeNull(types.string),
        updatedAt: types.maybeNull(types.string),
        updatedBy: types.maybeNull(types.string),
        mediaEvents: types.optional(types.array(MediaEvent), []),
        mediaUrl: types.maybeNull(types.string),
        hasMedia: types.maybeNull(types.boolean),
        success: types.maybeNull(types.boolean),
    })
    .views(self => ({
        get getExecutionData() {
            return getSnapshot(self);
        },
    }))
    .actions(self => {
        const { api } = getEnv<IStoresEnv>(self);

        const loadExecution = flow(function* (payload: any) {
            const result = yield performGetExecution(
                api,
                {
                    payload,
                    errorHandlers: { DEFAULT: '' },
                },
            );

            if(result.success){
                applySnapshot(self, {...result.data});

                return result.data;
            }

        });

        // Media URLs may point at private S3 objects: the resolver exchanges them for a
        // pre-signed URL (or echoes the same URL back for locally-hosted media). Falls back
        // to the original URL so playback keeps working if the resolver is unavailable.
        const resolveMediaUrl = flow(function* (mediaUrl?: string | null) {
            const targetUrl = mediaUrl ?? self.mediaUrl;

            if (!targetUrl) {
                return null;
            }

            if (!self.id) {
                return targetUrl;
            }

            const result = yield performResolveExecutionMedia(
                api,
                {
                    payload: { ExecutionId: self.id, MediaUrl: targetUrl },
                    errorHandlers: { DEFAULT: () => null },
                },
            );

            if (result.success) {
                return extractResolvedMediaUrl(result.data) ?? targetUrl;
            }

            return targetUrl;
        });

        return {
            loadExecution,
            resolveMediaUrl,
        }
    })

type ExecutionsStoreFactoryType = typeof ExecutionsStoreInferred;
interface IExecutionsStoreFactory extends ExecutionsStoreFactoryType { }
export const ExecutionsStore: IExecutionsStoreFactory = ExecutionsStoreInferred;
export interface IExecutionsStore extends Instance<IExecutionsStoreFactory> { }
export interface IExecutionsStoreSnapshotIn extends SnapshotIn<IExecutionsStore> { }
